import * as Yup from 'yup';
import { useMemo, useEffect } from 'react';
import { max, startOfDay } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';

import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { DatePicker } from '@mui/x-date-pickers';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { fCurrencyRupees } from 'src/utils/format-number';

import { useAppDispatcher } from 'src/redux/store';
import { createLoanPayment, updateLoanPayment } from 'src/redux/slices/loan-agreement';

import FormProvider, { RHFTextField } from 'src/components/hook-form';

import { ILoanPayment, ILoanAgreementItem } from 'src/types/loan-agreement';

import { toCents, earliestAllowedDate } from './utils';

type Props = {
  agreement: ILoanAgreementItem;
  // Present when an admin edits an existing payment.
  payment?: ILoanPayment | null;
  isAdmin: boolean;
  open: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
};

type FormValues = {
  paymentDate: Date | null;
  amount: number;
  note: string;
};

/**
 * Record (or, for admins, edit) an installment payment. Nothing is
 * pre-filled on a new payment; the remaining balance is shown as a hint
 * and caps the amount.
 */
export default function LoanPaymentDialog({
  agreement,
  payment,
  isAdmin,
  open,
  onClose,
  onSuccess,
}: Props) {
  const dispatch = useAppDispatcher();

  const isEdit = !!payment;

  // When editing, the payment's own amount is available again.
  const maxInCents = agreement.summary.remainingInCents + (payment?.amountInCents ?? 0);

  const loanPaidDate = startOfDay(new Date(agreement.loanPaidDate));
  const recentLimit = earliestAllowedDate(isAdmin);
  const minDate = recentLimit ? max([recentLimit, loanPaidDate]) : loanPaidDate;

  const schema = useMemo(
    () =>
      Yup.object().shape({
        paymentDate: Yup.date()
          .nullable()
          .required('Payment date is required')
          .max(new Date(), 'Payment date cannot be in the future')
          .test(
            'min-date',
            isAdmin
              ? 'Payment date cannot be before the loan paid date'
              : 'Payment date must be today or within the last 2 days',
            (value) => !value || startOfDay(value) >= minDate
          ),
        amount: Yup.number()
          .moreThan(0, 'Amount is required')
          .test(
            'max',
            `Amount cannot exceed ${fCurrencyRupees(maxInCents)}`,
            (value) => toCents(value) <= maxInCents
          ),
        note: Yup.string().max(500),
      }),
    [isAdmin, maxInCents, minDate]
  );

  const methods = useForm<FormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema as any),
    defaultValues: { paymentDate: null, amount: 0, note: '' },
  });

  const {
    reset,
    watch,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (open) {
      reset(
        payment
          ? {
              paymentDate: new Date(payment.paymentDate),
              amount: payment.amountInCents / 100,
              note: payment.note ?? '',
            }
          : { paymentDate: null, amount: 0, note: '' }
      );
    }
  }, [open, payment, reset]);

  const amountInCents = toCents(watch('amount'));
  const leftAfter = maxInCents - amountInCents;

  const onSubmit = handleSubmit(async (data) => {
    const body = {
      paymentDate: (data.paymentDate as Date).toISOString(),
      amountInCents: toCents(data.amount),
      note: data.note.trim() || undefined,
    };

    try {
      if (isEdit) {
        await dispatch(
          updateLoanPayment({ id: agreement._id, paymentId: payment._id, payment: body })
        ).unwrap();
        enqueueSnackbar('Payment updated');
      } else {
        await dispatch(createLoanPayment({ id: agreement._id, payment: body })).unwrap();
        enqueueSnackbar(leftAfter === 0 ? 'Payment recorded — loan settled' : 'Payment recorded');
      }
      onSuccess();
      onClose();
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message, { variant: 'error' });
    }
  });

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={onClose}>
      <DialogTitle>{isEdit ? 'Edit Installment' : 'Add Installment'}</DialogTitle>

      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Remaining to pay: <strong>{fCurrencyRupees(agreement.summary.remainingInCents)}</strong>
            {agreement.summary.nextDue && (
              <>
                <br />
                Next installment: {fCurrencyRupees(agreement.summary.nextDue.amountInCents)}
              </>
            )}
          </Typography>

          <Stack spacing={2}>
            <Controller
              name="paymentDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Payment Date"
                  value={field.value}
                  onChange={field.onChange}
                  disableFuture
                  minDate={minDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />

            <RHFTextField
              name="amount"
              label="Amount (Rupees)"
              type="number"
              required
              helperText={`Up to ${fCurrencyRupees(
                maxInCents
              )} — the full remaining balance can be paid at once`}
            />

            <RHFTextField name="note" label="Note (Optional)" multiline rows={2} />

            {amountInCents > 0 && leftAfter >= 0 && (
              <Alert severity={leftAfter === 0 ? 'success' : 'info'}>
                {leftAfter === 0
                  ? 'This payment settles the loan.'
                  : `${fCurrencyRupees(leftAfter)} will be left to pay after this payment.`}
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {isEdit ? 'Save' : 'Add Installment'}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
